import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import ContractContextNavigation from '../../components/ui/ContractContextNavigation';

// Import form components
import ContractBasicsForm from './components/ContractBasicsForm';
import PartyManagementForm from './components/PartyManagementForm';
import KeyTermsForm from './components/KeyTermsForm';
import FloatingActionBar from './components/FloatingActionBar';
import AutoSaveIndicator from './components/AutoSaveIndicator';
import ValidationSummary from './components/ValidationSummary';

const ContractEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Form state
  const [formData, setFormData] = useState({
    template: '',
    title: '',
    type: '',
    contractId: '',
    description: '',
    priority: 'medium',
    tags: '',
    parties: [],
    startDate: '',
    endDate: '',
    currency: 'USD',
    contractValue: '',
    paymentTerms: 'net-30',
    autoRenewal: false,
    renewalTerm: '1-year',
    renewalNoticePeriod: '30',
    terminationNotice: '30',
    terminationForConvenience: false,
    governingLaw: '',
    disputeResolution: '',
    specialConditions: '',
    includeConfidentiality: false,
    includeIntellectualProperty: false,
    includeForceMajeure: false
  });

  // UI state
  const [errors, setErrors] = useState({});
  const [warnings, setWarnings] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [showValidationSummary, setShowValidationSummary] = useState(false);

  // Check if editing existing contract
  const isEditing = location.state?.contractId || false;
  const contractId = location.state?.contractId || 'CT-2024-001';

  // Load existing contract data if editing
  useEffect(() => {
    if (isEditing) {
      // Mock loading existing contract data
      const mockContractData = {
        template: 'standard-service',
        title: 'Software Development Services Agreement',
        type: 'service-agreement',
        contractId: contractId,
        description: `Comprehensive software development services agreement for the design, development, and deployment of a custom web application platform. This contract covers all aspects of the development lifecycle including requirements gathering, system architecture, frontend and backend development, testing, deployment, and ongoing maintenance support.\n\nThe scope includes user interface design, database architecture, API development, third-party integrations, security implementation, and performance optimization. The deliverables will be provided in phases with regular milestone reviews and client approval processes.`,
        priority: 'high',
        tags: 'software, development, web application, custom platform',
        parties: [
          {
            id: '1',
            role: 'client',
            entityType: 'corporation',
            companyName: 'TechCorp Solutions Inc.',
            contactName: 'Sarah Johnson',
            title: 'Chief Technology Officer',
            email: 'sarah.johnson@techcorp.com',
            phone: '+1 (555) 123-4567',
            address: {
              street: '123 Innovation Drive, Suite 400',
              city: 'San Francisco',
              state: 'CA',
              zipCode: '94105',
              country: 'United States'
            },
            signatureRequired: true
          },
          {
            id: '2',
            role: 'vendor',
            entityType: 'llc',
            companyName: 'Digital Solutions LLC',
            contactName: 'Michael Chen',
            title: 'Managing Partner',
            email: 'michael.chen@digitalsolutions.com',
            phone: '+1 (555) 987-6543',
            address: {
              street: '456 Tech Park Boulevard',
              city: 'Austin',
              state: 'TX',
              zipCode: '78701',
              country: 'United States'
            },
            signatureRequired: true
          }
        ],
        startDate: '2024-02-01',
        endDate: '2024-08-31',
        currency: 'USD',
        contractValue: '125,000.00',
        paymentTerms: 'net-30',
        autoRenewal: true,
        renewalTerm: '6-months',
        renewalNoticePeriod: '60',
        terminationNotice: '30',
        terminationForConvenience: true,
        governingLaw: 'State of California, United States',
        disputeResolution: 'Binding Arbitration',
        specialConditions: `1. All source code and intellectual property developed under this agreement shall be owned by the Client upon final payment.\n\n2. The Vendor shall provide 90 days of post-deployment support and bug fixes at no additional cost.\n\n3. Any changes to the original scope of work must be documented in writing and approved by both parties before implementation.\n\n4. The Vendor shall maintain appropriate cybersecurity insurance coverage of at least $1,000,000 during the term of this agreement.`,
        includeConfidentiality: true,
        includeIntellectualProperty: true,
        includeForceMajeure: true
      };
      
      setFormData(mockContractData);
      setLastSaved(new Date(Date.now() - 300000)); // 5 minutes ago
      setLastAutoSave(new Date(Date.now() - 120000)); // 2 minutes ago
    }
  }, [isEditing, contractId]);

  // Form validation
  const validateForm = useCallback(() => {
    const newErrors = {};
    const newWarnings = {};

    // Required field validation
    if (!formData?.title?.trim()) {
      newErrors.title = 'Contract title is required';
    }

    if (!formData?.type) {
      newErrors.type = 'Contract type must be selected';
    }

    if (!formData?.description?.trim()) {
      newErrors.description = 'Contract description is required';
    } else if (formData?.description?.length < 50) {
      newWarnings.description = 'Description should be more detailed (at least 50 characters)';
    }

    if (!formData?.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (formData?.parties?.length === 0) {
      newErrors.parties = 'At least one party must be added to the contract';
    } else if (formData?.parties?.length === 1) {
      newWarnings.parties = 'Consider adding additional parties to complete the contract';
    }

    // Party validation
    formData?.parties?.forEach((party, index) => {
      if (!party?.role) {
        newErrors[`party_${index}_role`] = `Party ${index + 1} role is required`;
      }
      if (!party?.companyName?.trim()) {
        newErrors[`party_${index}_company`] = `Party ${index + 1} company name is required`;
      }
      if (!party?.contactName?.trim()) {
        newErrors[`party_${index}_contact`] = `Party ${index + 1} contact name is required`;
      }
      if (!party?.email?.trim()) {
        newErrors[`party_${index}_email`] = `Party ${index + 1} email is required`;
      }
    });

    // Contract value validation
    if (formData?.contractValue && isNaN(parseFloat(formData?.contractValue?.replace(/[^\d.]/g, '')))) {
      newErrors.contractValue = 'Contract value must be a valid number';
    }

    // Date validation
    if (formData?.startDate && formData?.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      if (endDate <= startDate) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    setWarnings(newWarnings);
    return Object.keys(newErrors)?.length === 0;
  }, [formData]);

  // Handle form data changes
  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
    
    // Clear field-specific errors when user starts typing
    if (errors?.[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        return newErrors;
      });
    }
  }, [errors]);

  // Auto-save functionality
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const autoSaveTimer = setTimeout(() => {
      setIsAutoSaving(true);
      
      // Simulate auto-save API call
      setTimeout(() => {
        setLastAutoSave(new Date());
        setIsAutoSaving(false);
      }, 1000);
    }, 30000); // Auto-save every 30 seconds

    return () => clearTimeout(autoSaveTimer);
  }, [formData, hasUnsavedChanges]);

  // Save draft
  const handleSaveDraft = async () => {
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      
      // Show success message (you could use a toast library here)
      console.log('Draft saved successfully');
    } catch (error) {
      console.error('Failed to save draft:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Save and continue
  const handleSaveAndContinue = async () => {
    const isValid = validateForm();
    
    if (!isValid) {
      setShowValidationSummary(true);
      return;
    }

    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      
      // Navigate to next step (milestone management)
      navigate('/milestone-management', { 
        state: { 
          contractId: formData?.contractId || contractId,
          contractData: formData 
        } 
      });
    } catch (error) {
      console.error('Failed to save contract:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel editing
  const handleCancel = () => {
    navigate('/contract-details', { state: { contractId } });
  };

  // Focus on field with error
  const handleFieldFocus = (fieldName) => {
    const element = document.querySelector(`[name="${fieldName}"]`);
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element?.focus();
    }
    setShowValidationSummary(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ContractContextNavigation 
        contractId={contractId}
        contractTitle={formData?.title || 'New Contract'}
      />
      <div className="pt-4 pb-24">
        <div className="max-w-4xl mx-auto px-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  {isEditing ? 'Edit Contract' : 'Create New Contract'}
                </h1>
                <p className="text-muted-foreground mt-1">
                  {isEditing 
                    ? 'Modify contract details and terms' 
                    : 'Define contract basics, parties, and key terms'
                  }
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                <AutoSaveIndicator
                  isAutoSaving={isAutoSaving}
                  lastAutoSave={lastAutoSave}
                  autoSaveEnabled={true}
                />
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowValidationSummary(!showValidationSummary)}
                  iconName={showValidationSummary ? 'EyeOff' : 'Eye'}
                  iconPosition="left"
                >
                  {showValidationSummary ? 'Hide' : 'Show'} Validation
                </Button>
              </div>
            </div>

            {/* Breadcrumb */}
            <nav className="flex items-center space-x-2 text-sm text-muted-foreground mt-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/contract-dashboard')}>
                Dashboard
              </Button>
              <Icon name="ChevronRight" size={14} />
              <Button variant="ghost" size="sm" onClick={() => navigate('/contract-details')}>
                Contract Details
              </Button>
              <Icon name="ChevronRight" size={14} />
              <span className="text-foreground">Editor</span>
            </nav>
          </div>

          {/* Validation Summary */}
          {showValidationSummary && (
            <div className="mb-8">
              <ValidationSummary
                errors={errors}
                warnings={warnings}
                onFieldFocus={handleFieldFocus}
              />
            </div>
          )}

          {/* Form Sections */}
          <div className="space-y-8">
            {/* Contract Basics */}
            <ContractBasicsForm
              formData={formData}
              onChange={handleFormChange}
              errors={errors}
            />

            {/* Party Management */}
            <PartyManagementForm
              formData={formData}
              onChange={handleFormChange}
              errors={errors}
            />

            {/* Key Terms */}
            <KeyTermsForm
              formData={formData}
              onChange={handleFormChange}
              errors={errors}
            />
          </div>

          {/* Help Section */}
          <div className="mt-12 bg-muted/30 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <Icon name="HelpCircle" size={20} className="text-accent mt-1" />
              <div>
                <h3 className="font-medium text-foreground mb-2">Need Help?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Our contract editor guides you through creating comprehensive legal agreements. 
                  All fields marked with an asterisk (*) are required for contract completion.
                </p>
                <div className="flex items-center space-x-4">
                  <Button variant="ghost" size="sm" iconName="Book" iconPosition="left">
                    View Documentation
                  </Button>
                  <Button variant="ghost" size="sm" iconName="MessageCircle" iconPosition="left">
                    Contact Support
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Floating Action Bar */}
      <FloatingActionBar
        hasUnsavedChanges={hasUnsavedChanges}
        onSaveDraft={handleSaveDraft}
        onSaveAndContinue={handleSaveAndContinue}
        onCancel={handleCancel}
        isLoading={isLoading}
        lastSaved={lastSaved}
      />
    </div>
  );
};

export default ContractEditor;